import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, ArrowLeft } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const params = new URLSearchParams(location.search);
  const confirmation = params.get('confirmation');
  const emailConfirmed = params.get('confirmed') === 'true';
  const redirect = params.get('redirect');
  const serviceId = params.get('service');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Erfolgreich angemeldet!');
      if (redirect === 'booking') {
        navigate(`/?booking=open${serviceId ? `&service=${encodeURIComponent(serviceId)}` : ''}`);
      } else if (redirect === 'angebote') {
        navigate('/angebote');
      } else {
        navigate('/profile');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Anmeldung fehlgeschlagen. Bitte prüfen Sie Ihre Anmeldedaten.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6 text-slate-600 hover:text-blue-600"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück zur Startseite
        </Button>

        <Card className="p-8 shadow-xl border-slate-100">
          <div className="text-center mb-8">
            <div className="mx-auto mb-4 flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_friseur-booking/artifacts/mtmj2lif_IMG_5702.PNG" 
                alt="Salon Galo Logo" 
                className="h-16 w-auto"
              />
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Willkommen zurück</h1>
            <p className="text-slate-600 mt-2">Melden Sie sich an, um einen Termin zu buchen</p>
          </div>

          {confirmation === 'pending' && (
            <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800" role="status">
              Wir haben Ihnen eine Bestätigungs-E-Mail geschickt. Bitte klicken Sie auf den Link in der E-Mail und prüfen Sie auch den Spam-Ordner.
            </div>
          )}

          {emailConfirmed && (
            <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800" role="status">
              Ihre E-Mail-Adresse wurde bestätigt. Sie können sich jetzt anmelden.
            </div>
          )}

          {redirect === 'booking' && (
            <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800" role="status">
              Bitte melden Sie sich an. Danach wird die Terminbuchung automatisch geöffnet.
            </div>
          )}
          {redirect === 'angebote' && (
            <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800" role="status">
              Bitte melden Sie sich an. Danach können Sie Ihr Abo oder Ihren Gutschein sicher kaufen.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                E-Mail
              </Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12 rounded-lg border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ihre@email.de"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                Passwort
              </Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-12 rounded-lg border-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 rounded-full text-base font-medium shadow-lg hover:shadow-blue-200 transition-all"
              data-testid="login-submit-button"
            >
              {loading ? 'Anmelden...' : 'Anmelden'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Noch kein Konto?{' '}
              <Link
                to={`/register${location.search}`}
                className="text-blue-600 hover:text-blue-700 font-medium"
                data-testid="register-link"
              >
                Jetzt registrieren
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
